import useTranslation from 'utils/hooks/useTranslation'
import numeral from 'numeral'
import { useAppSelector } from 'store/hooks'
import { useSalesNetwork } from 'utils/hooks/useSalesNetworks'
import { SvgResultHome } from 'components/svg'
import { LinkButton } from 'components/LinkButton'
import { useCallback } from 'react'

/** Parse distribution percentage — handles scientific notation strings like "7.105e-15" where the coefficient (7.105) is the intended value */
function parseDistribution(value: any): number {
  if (value == null) return 0;
  const str = String(value);
  const match = str.match(/^(-?\d+\.?\d*)[eE]-\d+$/);
  if (match) return parseFloat(match[1]) || 0;
  return parseFloat(str) || 0;
}

const HeatModeCalculatedNeeds: React.FC<{ deperditionCode: number, calculateDeperdAndFlowTemperature: () => void, trigger?: () => void }> = ({ deperditionCode, calculateDeperdAndFlowTemperature, trigger }) => {
  const { t } = useTranslation()
  const { isThermor } = useSalesNetwork()

  const projectStore = useAppSelector((state) => state.project)

  const handleCalculate = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    // call calculation if provided
    try {
      if (calculateDeperdAndFlowTemperature) await calculateDeperdAndFlowTemperature();
    } catch (e) {
      // ignore calculation errors here
    }

    // trigger validation if available
    try {
      if (trigger) await trigger();
    } catch (e) {
      // ignore
    }

    // find the first invalid field and scroll it into view (only on actual errors, not required fields)
    try {
      const selectors = '.is-invalid, [aria-invalid="true"], .error, .invalid, [data-invalid="true"], :invalid';
      const el: Element | null = document.querySelector(selectors);
      if (el) {
        const elHtml = el as HTMLElement;
        if (typeof elHtml.scrollIntoView === 'function') {
          elHtml.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        try { (elHtml as HTMLElement).focus(); } catch (e) { }
      }
    } catch (e) {
      // ignore DOM errors
    }
  }, [calculateDeperdAndFlowTemperature, trigger]);
  return projectStore.project.deperds?.Deperd ? (
    <group data-align="center" data-space-vertical="20">
      {deperditionCode != 4 ?
        isThermor ?
          <group data-space="40" data-space-vertical='30' data-gap="30" data-contain="" data-border-color="main-color-custom-border">
            <group data-gap="20" data-align="center">
              <text data-weight="700" data-text-size="xx-large" data-ellipsis="" data-color="grey-color-dark">
                {t("LOSS_KW")}
              </text>
              <text data-weight="600" data-text-size="xx-large" data-ellipsis="" data-color="main">
                {numeral(projectStore.project.deperds?.Deperd && projectStore.project.deperds?.Study.StudyDeperdition.round(0)).format()} W
              </text>
            </group>
            <group data-gap="10" data-text-size="larger" data-weight="700">
              <text data-ellipsis=""> {t('COEFFICIENT_OF_LOSS')} </text>
              <text data-ellipsis=""> {numeral(projectStore.project?.deperds?.G).format('0,0.00')} W/m3/K </text>
            </group>
            {projectStore.project.newMaximumTempTransmitter != null &&
              <group data-gap="10" data-text-size="large" data-weight="400">
                <text data-ellipsis=""> {t('NEW_START_TEMP')} </text>
                <text data-ellipsis=""> {projectStore.project.newMaximumTempTransmitter} °C </text>
              </group>
            }
          </group>
          :
          <group data-space="40" data-background="main-color-custom-lighter" data-radius="20" data-contain="">
            <group data-gap="20" data-align="center">
              <text data-weight="600" data-text-size="22" data-ellipsis="" data-color="grey-color-dark">
                {t("LOSS_KW")}
              </text>
              <text data-weight="600" data-text-size="40" data-ellipsis="" data-color="main">
                {numeral(projectStore.project.deperds?.Deperd && projectStore.project.deperds?.Study.StudyDeperdition.round(0)).format()} W
              </text>
            </group>
            <group data-gap="10">
              <group data-gap="10" data-text-size="large" data-weight="400">
                <text data-ellipsis=""> {t('COEFFICIENT_OF_LOSS')} </text>
                <text data-ellipsis=""> {numeral(projectStore.project?.deperds?.G).format('0,0.00')} W/m3/K </text>
              </group>
              {projectStore.project.newMaximumTempTransmitter != null &&
                <group data-gap="10" data-text-size="large" data-weight="400">
                  <text data-ellipsis=""> {t('NEW_START_TEMP')} </text>
                  <text data-ellipsis=""> {projectStore.project.newMaximumTempTransmitter} °C </text>
                </group>
              }
            </group>
            {projectStore.project?.flowTemperature?.zone1 != null && (
              <group data-gap="10" data-space-vertical="10" data-text-size="large" data-weight="400">
                <text data-ellipsis=""> {t('DEPART_TEMP_Z1')} </text>
                <text data-ellipsis=""> {projectStore.project?.flowTemperature?.zone1?.minTemperature} {t('°C_UNIT')} </text>
              </group>
            )}
            {projectStore.project?.flowTemperature?.zone2 != null && (
              <group data-gap="10" data-text-size="large" data-weight="400">
                <text data-ellipsis=""> {t('DEPART_TEMP_Z2')} </text>
                <text data-ellipsis=""> {projectStore.project?.flowTemperature?.zone2?.minTemperature} {t('°C_UNIT')} </text>
              </group>
            )}
          </group>
        :

        <group data-space="15" data-background="main-color-custom-lighter" data-radius="20" data-direction="row" data-justify='space-around' >
          <group data-align="start" data-direction='column' data-length="350" data-space-vertical='20'>

            <group data-align="start" data-direction='column'>
              <text data-text-size="22" data-ellipsis="" data-color="grey-color-dark" >
                {t("ESTIMATED_NEED")}
              </text>
              <group data-direction="column" data-gap="5" data-space-bottom='10'>
                <text data-weight="600" data-text-size="40" data-ellipsis="" data-color="main">
                  {numeral(projectStore.project.deperds?.Deperd && projectStore.project.deperds?.Study.StudyDeperdition.round(0)).format()} W
                </text>

                <LinkButton title={t("NEW_CALCUL_TEMP_DEPART")} data-weight="600" icon="refresh" data-text-size="large" onClick={(e: React.FormEvent) => handleCalculate(e)} />
              </group>
            </group>
            <group data-align="start" data-direction='row' data-gap="5">
              <text data-text-size="14" data-ellipsis="" data-color="grey-color-dark" >
                {t("COEFFICIENT_OF_LOSS")}:
              </text>
              <text data-text-size="14" data-ellipsis="" data-weight="600"> {numeral(projectStore.project?.deperds?.G).format('0,0.00')} W/m3/K </text>
            </group>
            {projectStore.project?.flowTemperature?.zone1 != null && (
              <group data-gap="10" data-space-vertical="10" data-text-size="14" data-weight="400">
                <text data-ellipsis=""> {t('DEPART_TEMP_Z1')} </text>
                <text data-ellipsis=""> {projectStore.project?.flowTemperature?.zone1?.minTemperature} {t('°C_UNIT')} </text>
              </group>
            )}
            {projectStore.project?.flowTemperature?.zone2 != null && (
              <group data-gap="10" data-text-size="14" data-weight="400">
                <text data-ellipsis=""> {t('DEPART_TEMP_Z2')} </text>
                <text data-ellipsis=""> {projectStore.project?.flowTemperature?.zone2?.minTemperature} {t('°C_UNIT')} </text>
              </group>
            )}
          </group>
          <group data-length="320" >
            <group data-gap="10" data-text-size="14" data-weight="400" data-space-top='10'>
              <text data-ellipsis=""> {t('RESULTS_THERMALBRIDGES')} </text>
              <text data-ellipsis=""> {numeral(parseDistribution(projectStore.project.deperds?.Study?.Distribution?.ThermalBridges)).format('0,0.00')} % </text>
            </group>
            <group data-radius="20" data-space="30" data-justify="center" data-align="center" data-length="310" data-ratio="1:1">
              <picture data-position="absolute" >
                <SvgResultHome />
              </picture>
              <group data-align="center" data-justify="space-around" data-ratio="1:1">
                <group data-position="absolute" data-top="0" data-right="190" data-width="auto">
                  <text data-length="100" data-wrap="wrap">{t("RESULTS_CEILING")}</text>
                  <text data-length="100" data-wrap="wrap" data-weight="600">
                    {numeral(parseDistribution(projectStore.project.deperds?.Study?.Distribution?.Ceiling)).format('0,0.00')} %
                  </text>
                </group>
                <group data-position="absolute" data-top="center" data-right="190" data-width="auto">
                  <text data-length="100" data-wrap="wrap">{t("RESULTS_VENTILATION")}</text>
                  <text data-length="100" data-wrap="wrap" data-weight="600">
                    {numeral(parseDistribution(projectStore.project.deperds?.Study?.Distribution?.Ventilation)).format('0,0.00')} %
                  </text>
                </group>
                <group data-position="absolute" data-right="190" data-width="auto" data-bottom="40">
                  <text data-length="100" data-wrap="wrap">{t("RESULTS_WALL")}</text>
                  <text data-length="100" data-wrap="wrap" data-weight="600">
                    {numeral(parseDistribution(projectStore.project.deperds?.Study?.Distribution?.Walls)).format('0,0.00')} %
                  </text>
                </group>
                <group data-position="absolute" data-top="5" data-right="0" data-left="230" data-width="auto">
                  <text data-length="100" data-wrap="wrap">{t("RESULTS_WINDOW")}</text>
                  <text data-length="100" data-weight="600">
                    {numeral(parseDistribution(projectStore.project.deperds?.Study?.Distribution?.Windows)).format('0,0.00')} %
                  </text>
                </group>
                <group data-position="absolute" data-top="80" data-right="0" data-left="230" data-width="auto" >
                  <text data-length="100" data-wrap="wrap">{t("RESULTS_DOOR")}</text>
                  <text data-length="100" data-weight="600">
                    {numeral(parseDistribution(projectStore.project.deperds?.Study?.Distribution?.Doors)).format('0,0.00')} %
                  </text>
                </group>
                <group data-position="absolute" data-left="230" data-width="auto" data-bottom="60">
                  <text data-length="100" data-wrap="wrap">{t("RESULTS_FLOOR")}</text>
                  <text data-length="100" data-weight="600">
                    {numeral(parseDistribution(projectStore.project.deperds?.Study?.Distribution?.Floor)).format('0,0.00')} %
                  </text>
                </group>
              </group>
            </group>

          </group>
        </group>

      }

    </group>
  ) : (
    <> </>
  )
}

export default HeatModeCalculatedNeeds
